import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ReactComponent as GroupIcon } from "../../assets/icons/group-icon.svg";
import { ReactComponent as DotsVertical } from "../../assets/icons/dots-vertical.svg";
import { fetchAssetsByGroupId } from "../../store/assetsSlice";
import { modalViewsConst, setModalState } from "../../store/modalSlice";
import { imageCache } from "../../utils/imageCache";
import { getOwnerFromId } from "../../util/utils";
import type { AppDispatch, RootState } from "../../store";

// This component represents the main widget in the Main Dashboard that lists all asset groups with key information and actions.
type GroupWidgetProps = {
  searchQuery?: string;
};

const GroupWidget = ({ searchQuery = "" }: GroupWidgetProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [groupImageUrls, setGroupImageUrls] = useState<Record<string, string>>({});
  const [ownerNames, setOwnerNames] = useState<Record<string, string>>({});

  const assetGroups = useSelector((state: RootState) => state.assetGroups) as any;
  const assets = useSelector((state: RootState) => state.assets) as any;
  const user = useSelector((state: RootState) => state.user) as any;

  const groups = useMemo(() => Object.values(assetGroups?.groups || {}), [assetGroups?.groups]);

  useEffect(() => {
    // fetches assets for all groups when the component mounts or when the list of groups changes.
    groups.forEach((group: any) => {
      if (!assets?.dataGroups?.[group.id]) {
        dispatch(fetchAssetsByGroupId(group.id));
      }
    });
  }, [groups, assets?.dataGroups, dispatch]);

  useEffect(() => {
    // fetches signed URLs for group profile pictures and stores them in local state.
    let isMounted = true;

    const fetchGroupImages = async () => {
      const imageEntries = await Promise.all(
        groups.map(async (group: any) => {
          if (!group?.profilePictureUrl) {
            return null;
          }
          try {
            const signedUrl = await imageCache.getSignedUrl(group.profilePictureUrl);
            if (!signedUrl) {
              return null;
            }
            return [group.id, signedUrl] as const;
          } catch (error) {
            return null;
          }
        })
      );

      if (!isMounted) {
        return;
      }

      const nextImageUrls: Record<string, string> = {};
      imageEntries.forEach((entry) => {
        if (entry) {
          nextImageUrls[entry[0]] = entry[1];
        }
      });
      setGroupImageUrls(nextImageUrls);
    };

    fetchGroupImages();
    return () => {
      isMounted = false;
    };
  }, [groups]);

  useEffect(() => {
    // fetches owner names for user-owned groups so Owner column can show specific user names.
    let isMounted = true;

    const fetchOwnerNames = async () => {
      const ownerIds = Array.from(
        new Set(
          groups
            .filter((group: any) => !group?.organizationId && group?.owner)
            .map((group: any) => group.owner)
        )
      );

      if (ownerIds.length === 0) {
        if (isMounted) {
          setOwnerNames({});
        }
        return;
      }

      const entries = await Promise.all(
        ownerIds.map(async (ownerId: string) => {
          try {
            const ownerDetails = await getOwnerFromId(ownerId);
            const displayName = ownerDetails?.name || ownerDetails?.username || ownerDetails?.email || "Unknown User";
            return [ownerId, displayName] as const;
          } catch (error) {
            return [ownerId, "Unknown User"] as const;
          }
        })
      );

      if (!isMounted) {
        return;
      }

      const nextOwnerNames: Record<string, string> = {};
      entries.forEach(([ownerId, displayName]) => {
        nextOwnerNames[ownerId] = displayName;
      });
      setOwnerNames(nextOwnerNames);
    };

    fetchOwnerNames();
    return () => {
      isMounted = false;
    };
  }, [groups]);

  useEffect(() => {
    // Closes any open action menus when clicking outside of them.
    const onDocumentClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenuId(null);
      }
    };

    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, []);

  const handleImageError = (groupId: string) => {
    // If a group profile picture fails to load, this function removes the cached URL so that the default icon will be shown instead.
    setGroupImageUrls((prev) => {
      if (!prev[groupId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[groupId];
      return next;
    });
  };

  const currentUserId = user?.permissionDetails?.id;
  const isAdmin = user?.details?.groups?.includes("Admin");

  const handleRowClick = (groupId: string) => {
    navigate(`/home/asset-dashboard/${groupId}`);
  };

  const handleEdit = (group: any) => {
    const canEdit = isAdmin || (currentUserId && group.owner === currentUserId);
    const isOrganizationOwned = group?.organizationId && !canEdit;
    if (isOrganizationOwned) {
      return;
    }

    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.editAssetGroup,
        properties: group,
      })
    );
    setActiveMenuId(null);
  };

  const handleDelete = (group: any) => {
    const isDeleteDisabled = group?.organizationId && !isAdmin;
    if (isDeleteDisabled) {
      return;
    }

    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.deleteAssetGroup,
        properties: { ...group, __modalSize: "sm" },
      })
    );
    setActiveMenuId(null);
  };

  const getGroupLevelType = (group: any) => {
    // this function derives the group level type. (TODO: we should have group type in the backend. frontend is ready.)
    const candidate = group?.type || group?.assetType || group?.groupType;
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      return candidate.trim();
    }
    return null;
  };

  const filteredGroups = useMemo(() => {
    // this filters the groups based on the search query matching against group name, type, or organization name.
    const term = searchQuery.trim().toLowerCase();
    if (!term) {
      return groups;
    }

    return groups.filter((group: any) => {
      const groupName = (group?.name || "").toLowerCase();
      const groupType = (getGroupLevelType(group) || "").toLowerCase();
      const organizationName = group?.organizationId ? (user?.organizations?.data?.[group.organizationId]?.name || "").toLowerCase() : "";

      return groupName.includes(term) || groupType.includes(term) || organizationName.includes(term);
    });
  }, [searchQuery, groups, user?.organizations?.data]);

  const visibleGroups = useMemo(
    () =>
      filteredGroups.map((group: any) => {
        const groupAssets = assets?.dataGroups?.[group.id] || [];
        const canEdit = isAdmin || (currentUserId && group.owner === currentUserId);
        return {
          group,
          groupAssets,
          isOrganizationOwned: group?.organizationId && !canEdit,
          isDeleteDisabled: group?.organizationId && !isAdmin,
        };
      }),
    [filteredGroups, assets?.dataGroups, isAdmin, currentUserId]
  );

  const renderGroupIdentity = (group: any) => (
    <div className="flex min-w-0 items-center gap-2 sm:gap-3">
      <div className="flex h-10 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white sm:w-16">
        {groupImageUrls[group.id] ? (
          <img src={groupImageUrls[group.id]} alt={group.name} className="h-full w-full object-cover" onError={() => handleImageError(group.id)} />
        ) : (
          <GroupIcon className="h-5 w-5 text-slate-500" />
        )}
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm leading-5 text-slate-900">{group.name}</p>
      </div>
    </div>
  );

  const getOwnerLabel = (group: any) => {
    if (group?.organizationId) {
      return user?.organizations?.data?.[group.organizationId]?.name || "Organization";
    }
    if (group?.owner) {
      return ownerNames[group.owner] || "Unknown User";
    }
    return "Unknown User";
  };

  return (// The main table structure for displaying groups, with conditional rendering for empty state and action menus.
    <div className="flex w-full min-w-0 flex-col overflow-hidden rounded-[8px] border border-slate-200 bg-white">
      <div className="w-full overflow-x-auto">
        <table className="w-full table-auto border-collapse">
          <thead className="bg-slate-50">
            <tr className="border-b border-slate-200">
              <th className="min-w-[180px] px-4 py-3 text-left text-sm font-semibold text-slate-700 sm:min-w-[240px]">Asset Group</th>
              <th className="min-w-[90px] whitespace-nowrap px-4 py-3 text-left text-sm font-semibold text-slate-700 sm:min-w-[110px]">Asset count</th>
              <th className="min-w-[140px] px-4 py-3 text-left text-sm font-semibold text-slate-700 sm:min-w-[180px]">Owner</th>
              <th className="min-w-[56px] px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {visibleGroups.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-12 text-center text-sm text-slate-500">
                  No groups found.
                </td>
              </tr>
            ) : (
              visibleGroups.map(({ group, groupAssets, isOrganizationOwned, isDeleteDisabled }: any) => {
                return (
                  <tr
                    key={group.id}
                    className="cursor-pointer border-b border-slate-200 last:border-b-0 hover:bg-slate-50"
                    onClick={() => handleRowClick(group.id)}
                  >
                    <td className="px-3 py-2 align-middle">{renderGroupIdentity(group)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-slate-700 align-middle">{groupAssets.length}</td>
                    <td className="px-3 py-2 text-sm align-middle">
                      <span className="inline-block max-w-full truncate rounded-[10px] bg-green-100 px-1 text-green-600">
                        {getOwnerLabel(group)}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <div className="relative" ref={activeMenuId === group.id ? menuRef : null}>
                        <button
                          type="button"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-700 hover:bg-slate-200"
                          onClick={(event) => {
                            event.stopPropagation();
                            setActiveMenuId((prev) => (prev === group.id ? null : group.id));
                          }}
                          aria-label={`Open actions for ${group.name}`}
                        >
                          <DotsVertical className="h-4 w-4" />
                        </button>
                        {activeMenuId === group.id && (
                          <div className="absolute right-0 z-10 mt-1 w-36 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                            <button
                              type="button"
                              className={`w-full px-3 py-2 text-left text-sm ${
                                isOrganizationOwned ? "cursor-not-allowed text-slate-400" : "text-slate-700 hover:bg-slate-100"
                              }`}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleEdit(group);
                              }}
                              disabled={isOrganizationOwned}
                            >
                              Edit Group
                            </button>
                            <button
                              type="button"
                              className={`w-full px-3 py-2 text-left text-sm ${
                                isDeleteDisabled ? "cursor-not-allowed text-slate-400" : "text-rose-600 hover:bg-slate-100"
                              }`}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDelete(group);
                              }}
                              disabled={isDeleteDisabled}
                            >
                              Delete Group
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GroupWidget;
